import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, User, Check, IndianRupee, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { useAuthStore } from '@/app/store/authStore';
import {
    fetchCustomers,
    createReceipt,
    type CustomerItem,
} from '@/app/services/receiptService';
import { MobileHeader } from '@/components/layout/MobileHeader';
import { MobileInput } from '@/components/ui/MobileInput';
import { MobileButton } from '@/components/ui/MobileButton';
import { MobileEmptyState } from '@/components/ui/MobileEmptyState';
import { cn } from '@/lib/utils';

function useDebounced<T>(value: T, delay = 300): T {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return debounced;
}

export default function RecordReceiptPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const queryClient = useQueryClient();
    const user = useAuthStore((state) => state.user);

    const sites = user?.sites ?? [];

    // Pre-population from a delivery challan ("Record Cash Receipt" button)
    const prefillPartyId = searchParams.get('party_id');
    const prefillSiteId = searchParams.get('site_id');

    const initialSiteId = useMemo(() => {
        const requested = prefillSiteId ? Number(prefillSiteId) : undefined;
        if (requested && sites.some((s) => s.id === requested)) return requested;
        return sites[0]?.id;
    }, [prefillSiteId, sites]);

    const initialCustomer = useMemo<CustomerItem | null>(() => {
        if (!prefillPartyId) return null;
        return {
            id: Number(prefillPartyId),
            name: searchParams.get('party_name') ?? 'Selected customer',
            phone: searchParams.get('party_phone'),
        };
    }, [prefillPartyId]);

    const [siteId, setSiteId] = useState<number | undefined>(initialSiteId);

    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounced(search, 300);
    const [selected, setSelected] = useState<CustomerItem | null>(initialCustomer);

    const [amount, setAmount] = useState('');
    const [reference, setReference] = useState('');
    const [notes, setNotes] = useState('');

    // The customer's WhatsApp confirmation is deliberately NOT the agent's call.
    // It goes out when the office approves the collection and the receipt posts,
    // so a toggle here would either do nothing visible (verified mode) or hand
    // the agent a customer-communication decision that belongs to the office.
    // The API field defaults to true; the org-level ConfigKV switch governs it,
    // and a customer with no valid mobile is skipped server-side anyway.

    const customersQuery = useQuery({
        queryKey: ['field-agent-customers', siteId, debouncedSearch],
        queryFn: () => fetchCustomers(siteId as number, debouncedSearch, 20),
        enabled: !!siteId && !selected && debouncedSearch.trim().length >= 1,
    });

    const mutation = useMutation({
        mutationFn: () =>
            createReceipt(siteId as number, {
                party_id: (selected as CustomerItem).id,
                amount: Number(amount),
                reference_number: reference.trim() || undefined,
                narration: notes.trim() || undefined,
            }),
        onSuccess: (result) => {
            queryClient.invalidateQueries({ queryKey: ['delivery-challans'] });
            queryClient.invalidateQueries({ queryKey: ['my-collections'] });
            // Two possible outcomes: queued for the office to check against the
            // cash (the normal case), or posted immediately. Never claim a
            // receipt number that does not exist yet.
            if (result.voucher) {
                toast.success(`Receipt ${result.voucher.voucher_number} recorded for ${selected?.name}.`);
                navigate('/dashboard');
            } else {
                // Number(): the API serializes Decimal as a string, and
                // String.prototype.toLocaleString returns it unformatted — the
                // toast would read "10000.00" instead of "10,000".
                toast.success(
                    `₹${Number(result.amount).toLocaleString('en-IN')} recorded for ${selected?.name}. ` +
                    `Pending office confirmation.`,
                );
                navigate('/collections');
            }
        },
        onError: (err) => {
            let message = 'Could not record the receipt. Please try again.';
            if (axios.isAxiosError(err)) {
                message = (err.response?.data as { detail?: string })?.detail ?? message;
            }
            toast.error(message);
        },
    });

    const amountNum = Number(amount);
    const canSubmit = useMemo(
        () => !!siteId && !!selected && amountNum > 0 && !mutation.isPending,
        [siteId, selected, amountNum, mutation.isPending],
    );

    if (!siteId) {
        return (
            <div className="flex flex-col min-h-full">
                <MobileHeader title="Record Receipt" showBack />
                <div className="flex-1 mobile-scroll-area pb-tab-bar">
                    <div className="p-4">
                        <MobileEmptyState
                            icon={<IndianRupee className="h-8 w-8" />}
                            title="No site assigned"
                            description="You are not assigned to a site yet. Please contact your administrator to record receipts."
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        // h-full, not min-h-full: .mobile-scroll-area is `flex-1 overflow-y-auto`,
        // which only becomes a real scroll container when its flex parent has a
        // DEFINITE height. With min-height the parent sizes to its content, the
        // scroll area grows with it and never scrolls, so a long form overflows
        // underneath the fixed bottom tab bar — putting the submit button out of
        // reach. The parent <main> has a definite height, so h-full resolves.
        <div className="flex flex-col h-full">
            <MobileHeader title="Record Receipt" showBack />

            <div className="flex-1 mobile-scroll-area pb-tab-bar">
                <div className="p-4 space-y-6">
                    {/* Site selector (only when multiple sites) */}
                    {sites.length > 1 && (
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-foreground">Site</label>
                            <select
                                className="mobile-input w-full"
                                value={siteId}
                                onChange={(e) => {
                                    setSiteId(Number(e.target.value));
                                    setSelected(null);
                                }}
                            >
                                {sites.map((s) => (
                                    <option key={s.id} value={s.id}>
                                        {s.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Customer selection */}
                    <div className="space-y-3">
                        <h3 className="mobile-section-header">Customer</h3>

                        {selected ? (
                            <div className="mobile-card flex items-center justify-between">
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                        <User className="h-4 w-4 text-primary" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="font-medium truncate">{selected.name}</p>
                                        {selected.phone && (
                                            <p className="text-xs text-muted-foreground truncate">
                                                {selected.phone}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <button
                                    onClick={() => {
                                        setSelected(null);
                                        setSearch('');
                                    }}
                                    className="text-sm text-primary font-medium px-2 py-1"
                                >
                                    Change
                                </button>
                            </div>
                        ) : (
                            <>
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <input
                                        className="mobile-input w-full pl-9"
                                        placeholder="Search customer by name or phone"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        autoFocus
                                    />
                                </div>

                                {debouncedSearch.trim().length >= 1 && (
                                    <div className="space-y-2">
                                        {customersQuery.isFetching ? (
                                            <div className="flex items-center justify-center py-6 text-muted-foreground">
                                                <Loader2 className="h-5 w-5 animate-spin" />
                                            </div>
                                        ) : customersQuery.data?.items.length ? (
                                            customersQuery.data.items.map((c) => (
                                                <button
                                                    key={c.id}
                                                    onClick={() => setSelected(c)}
                                                    className="mobile-card w-full text-left active:scale-[0.98] transition-all"
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center shrink-0">
                                                            <User className="h-4 w-4 text-muted-foreground" />
                                                        </div>
                                                        <div className="min-w-0">
                                                            <p className="font-medium truncate">{c.name}</p>
                                                            {c.phone && (
                                                                <p className="text-xs text-muted-foreground truncate">
                                                                    {c.phone}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </button>
                                            ))
                                        ) : (
                                            <p className="text-sm text-muted-foreground text-center py-4">
                                                No customers found
                                            </p>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Amount + details (enabled after selecting a customer) */}
                    {selected && (
                        <div className="space-y-4">
                            <h3 className="mobile-section-header">Payment Details</h3>

                            <MobileInput
                                label="Amount (₹)"
                                type="number"
                                inputMode="decimal"
                                min="0"
                                step="0.01"
                                placeholder="0.00"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                            />

                            <MobileInput
                                label="Reference (optional)"
                                placeholder="e.g. cash, txn id"
                                value={reference}
                                onChange={(e) => setReference(e.target.value)}
                            />

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-foreground">
                                    Notes (optional)
                                </label>
                                <textarea
                                    className="mobile-input w-full min-h-[80px] resize-none"
                                    placeholder="Any notes about this receipt"
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                />
                            </div>

                            <p className="text-xs text-muted-foreground">
                                Recorded as an advance receipt — no invoice is settled. Hand the cash
                                to the office; they confirm it against what you hand over.
                            </p>

                            <MobileButton
                                variant="primary"
                                className={cn('w-full')}
                                disabled={!canSubmit}
                                isLoading={mutation.isPending}
                                onClick={() => mutation.mutate()}
                                icon={<Check className="h-5 w-5" />}
                            >
                                Record Receipt
                            </MobileButton>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
